import { EmailMessageService } from '@core/services';
import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { plainToInstance } from 'class-transformer';
import { EmailMessageEntity } from '@core/entities';
import { GenerateImageUrlWithToken, GenerateMessageAttachmentUrl, isImageTypeAllowedByName } from '@core/utils';
import { SectionType } from '@core/enums';

export class UseEmailMessage {
  private readonly _emailMessageService = inject(EmailMessageService);

  // ------------------------------------------------- PERSONAL EMAIL TYPE ------------------------------------------------------

  getAllPersonalInboxMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalInbox().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllPersonalStarredMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalStarred().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllPersonalSentMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalSent().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllPersonalDraftMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalDraft().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllPersonalTrashMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalTrash().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllPersonalAllMailMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getPersonalAllMail().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  // ------------------------------------------------- CLUB EMAIL TYPE ------------------------------------------------------

  getAllClubInboxMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubInbox().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllClubStarredMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubStarred().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllClubSentMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubSent().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllClubDraftMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubDraft().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllClubTrashMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubTrash().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllClubAllMailMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getClubAllMail().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  // ------------------------------------------------- GROUP EMAIL TYPE ------------------------------------------------------

  getAllGroupInboxMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupInbox().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllGroupStarredMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupStarred().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllGroupSentMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupSent().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllGroupDraftMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupDraft().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllGroupTrashMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupTrash().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  getAllGroupAllMailMessages(): Observable<EmailMessageEntity[]> {
    return this._emailMessageService.getGroupAllMail().pipe(
      map((response: any) => {
        return response
          .map((item: any) => {
            const transformedItem = plainToInstance(EmailMessageEntity, item);
            transformedItem.isAttachmentImage = transformedItem.fileDetails && isImageTypeAllowedByName(transformedItem.fileDetails);
            if (transformedItem.fileDetails) {
              transformedItem.attachmentUrl = GenerateMessageAttachmentUrl(transformedItem.fileDetails);
            }
            if (transformedItem.group) {
              transformedItem.group.imageUrl = GenerateImageUrlWithToken(transformedItem.group.id, SectionType.GROUP);
            }
            return transformedItem;
          })
          .reverse();
      }),
    );
  }

  // ------------------------------------------------- MOVE MESSAGES ------------------------------------------------------

  moveMessageToStarred(esDbId: string, messageId: number) {
    return this._moveMessageToFolder(esDbId, messageId, 'starred');
  }

  moveMessageToTrash(esDbId: string, messageId: number) {
    return this._moveMessageToFolder(esDbId, messageId, 'trash');
  }

  moveMessageToSent(esDbId: string, messageId: number) {
    return this._moveMessageToFolder(esDbId, messageId, 'sent');
  }

  moveMessageToDraft(esDbId: string, messageId: number) {
    return this._moveMessageToFolder(esDbId, messageId, 'draft');
  }

  moveMessageToInbox(esDbId: string, messageId: number) {
    return this._moveMessageToFolder(esDbId, messageId, 'inbox');
  }

  // ------------------------------------------------- OTHER METHODS ------------------------------------------------------

  readMessage(messageId: number) {
    return this._emailMessageService.readMessage(messageId);
  }

  deleteDraft(messageId: number) {
    return this._emailMessageService.deleteDraft(messageId);
  }

  deleteMessage(esDbId: string) {
    return this._emailMessageService.deleteMessage(esDbId);
  }

  sendPersonalMessage(formData: FormData) {
    return this._emailMessageService.sendPersonalMessage(formData);
  }

  sendGroupMessage(formData: FormData) {
    return this._emailMessageService.sendGroupMessage(formData);
  }

  sendClubMessage(formData: FormData) {
    return this._emailMessageService.sendClubMessage(formData);
  }

  replyToMessage(esDbId: string, formData: FormData) {
    return this._emailMessageService.replyMessage(esDbId, formData);
  }

  replyToAllMessage(esDbId: string, formData: FormData) {
    return this._emailMessageService.replyToAllMessage(esDbId, formData);
  }

  markEmailMessagesAsRead() {
    return this._emailMessageService.markAsRead();
  }

  private _moveMessageToFolder(esDbId: string, messageId: number, folder: string) {
    if (!esDbId || !messageId || !folder) {
      throw new Error('Invalid data');
    }
    const data = {
      esdb_id: esDbId,
      id: messageId,
      to: folder,
    };
    return this._emailMessageService.moveMessage(data);
  }
}
